// Clan rules tab (web parity for iOS ClanRulesView): rule cards, a stale-
// acceptance banner with one-tap accept, and admin add/edit/delete.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  acceptClanRules,
  createClanRule,
  deleteClanRule,
  listClanRules,
  updateClanRule,
} from "@/api/clans";
import type { ClanRuleRead, ClanRuleTimeWindow } from "@/api/community-types";
import { Alert, Button, Input, Spinner, cx } from "@/components/ui";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const DAY_LABEL: Record<string, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

function formatWindow(w: ClanRuleTimeWindow): string {
  const days = DAYS.filter((d) => w.days.includes(d)).map((d) => DAY_LABEL[d]).join(", ");
  return days ? `${days} ${w.start}–${w.end}` : `${w.start}–${w.end}`;
}

export function RulesPanel({ clanId, canManage }: { clanId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ClanRuleRead | "new" | null>(null);

  const rulesQuery = useQuery({ queryKey: ["clan-rules", clanId], queryFn: () => listClanRules(clanId) });
  const rules = rulesQuery.data ?? [];
  const stale = rules.filter((r) => r.my_acceptance && r.my_acceptance.accepted_version < r.version);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["clan-rules", clanId] });

  const acceptMut = useMutation({
    mutationFn: () => acceptClanRules(clanId, Object.fromEntries(rules.map((r) => [r.id, r.version]))),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (ruleId: string) => deleteClanRule(clanId, ruleId),
    onSuccess: invalidate,
  });

  if (rulesQuery.isLoading) {
    return (
      <div className="flex justify-center py-12 text-brand-600">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {stale.length > 0 && (
        <div className="flex items-center gap-3 rounded-xl bg-amber-50 p-3 text-sm">
          <span className="flex-1 font-medium text-amber-800">
            {t("Rules updated — review and re-accept.")}
          </span>
          <Button size="sm" loading={acceptMut.isPending} onClick={() => acceptMut.mutate()}>
            {t("Accept")}
          </Button>
        </div>
      )}

      {rules.length === 0 && !editing && (
        <p className="py-6 text-center text-sm text-ink-faint">{t("No rules yet.")}</p>
      )}

      {rules.map((rule) => {
        const isStale = !!rule.my_acceptance && rule.my_acceptance.accepted_version < rule.version;
        return (
          <div key={rule.id} className="card p-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-ink">{rule.title}</h3>
              {canManage && (
                <div className="flex shrink-0 gap-1">
                  <button className="text-xs text-brand-600 hover:underline" onClick={() => setEditing(rule)}>
                    {t("Edit")}
                  </button>
                  <button
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => deleteMut.mutate(rule.id)}
                  >
                    {t("Delete")}
                  </button>
                </div>
              )}
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink-soft">{rule.body}</p>
            {rule.time_window && (
              <span className="mt-2 inline-block rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                {formatWindow(rule.time_window)}
              </span>
            )}
            <p className={cx("mt-2 text-xs", isStale ? "text-red-600" : "text-ink-faint")}>
              {isStale
                ? t("Updated since you accepted")
                : rule.my_acceptance
                  ? t("Accepted")
                  : ""}
            </p>
          </div>
        );
      })}

      {editing ? (
        <RuleForm
          clanId={clanId}
          rule={editing === "new" ? null : editing}
          onDone={() => {
            setEditing(null);
            invalidate();
          }}
          onCancel={() => setEditing(null)}
        />
      ) : (
        canManage && (
          <Button size="sm" variant="secondary" onClick={() => setEditing("new")}>
            {t("Add rule")}
          </Button>
        )
      )}
    </div>
  );
}

function RuleForm({
  clanId,
  rule,
  onDone,
  onCancel,
}: {
  clanId: string;
  rule: ClanRuleRead | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(rule?.title ?? "");
  const [body, setBody] = useState(rule?.body ?? "");
  const [windowed, setWindowed] = useState(!!rule?.time_window);
  const [days, setDays] = useState<string[]>(rule?.time_window?.days ?? []);
  const [start, setStart] = useState(rule?.time_window?.start ?? "09:00");
  const [end, setEnd] = useState(rule?.time_window?.end ?? "11:00");
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const tw: ClanRuleTimeWindow | null =
        windowed && days.length > 0 ? { start, end, days, tz: "local" } : null;
      if (rule) {
        return updateClanRule(clanId, rule.id, {
          title: title.trim(),
          body: body.trim(),
          ...(tw ? { time_window: tw } : { clear_time_window: true }),
        });
      }
      return createClanRule(clanId, { title: title.trim(), body: body.trim(), time_window: tw });
    },
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof Error ? e.message : t("Couldn't save.")),
  });

  return (
    <div className="card space-y-3 p-4">
      {err && <Alert>{err}</Alert>}
      <Input label={t("Title")} value={title} onChange={(e) => setTitle(e.target.value)} />
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Body")}</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          className="w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={windowed}
          onChange={(e) => setWindowed(e.target.checked)}
          className="h-4 w-4 rounded border-black/20 text-brand-600"
        />
        {t("Active during specific hours")}
      </label>
      {windowed && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]))}
                className={cx(
                  "rounded-lg px-2 py-1 text-xs font-semibold",
                  days.includes(d) ? "bg-brand-600 text-white" : "bg-black/5 text-ink-soft",
                )}
              >
                {DAY_LABEL[d]}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-lg border border-black/10 px-2 py-1 text-sm" />
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-lg border border-black/10 px-2 py-1 text-sm" />
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!title.trim() || !body.trim()}
          loading={save.isPending}
          onClick={() => {
            setErr(null);
            save.mutate();
          }}
        >
          {t("Save")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}
